import { TextEmbeddingFunction, getRegistry } from '@lancedb/lancedb/embedding';
import { Float32 } from 'apache-arrow';
import { EmbeddingEngine, buildEmbeddingConfig, getFinetunedModelPath } from './embeddings.js';
import { getConfiguredModelId } from '../models/index.js';
import type { EmbeddingConfig } from '../types/config.js';

/**
 * Custom embedding function options that map to our EmbeddingConfig.
 * Stored in LanceDB table metadata for provenance tracking.
 */
export interface HuggingFaceEmbeddingOptions {
  readonly model: string;
  readonly batchSize: number;
  readonly dtype: 'fp32' | 'fp16' | 'q8' | 'q4';
  readonly pooling: 'mean' | 'cls' | 'none';
  readonly normalize: boolean;
  readonly queryPrefix: string;
  readonly docPrefix: string;
}

/**
 * LanceDB embedding function wrapping our EmbeddingEngine.
 *
 * This provides:
 * - Automatic query embedding via table.search(query)
 * - Model provenance stored in table metadata
 * - Consistent query/document embedding through single code path
 * - Support for asymmetric models with query/doc prefixes
 */
export class HuggingFaceEmbeddingFunction extends TextEmbeddingFunction<HuggingFaceEmbeddingOptions> {
  private readonly engine: EmbeddingEngine;
  private readonly embeddingConfig: EmbeddingConfig;
  private _ndims: number | null = null;

  constructor(optionsRaw?: Partial<HuggingFaceEmbeddingOptions>) {
    super();
    // Resolve any variables in options (LanceDB feature)
    const options = this.resolveVariables(optionsRaw ?? {});

    // BK_FINETUNED_MODEL ALWAYS takes priority over stored options (table metadata)
    // This ensures env var overrides work even when LanceDB recreates from registry
    // Priority: env var > stored options > configured default
    const finetunedPath = getFinetunedModelPath();
    const model = finetunedPath ?? options.model ?? getConfiguredModelId();

    // Build overrides object, only including defined values (exactOptionalPropertyTypes compliance)
    const overrides: Partial<Omit<EmbeddingConfig, 'model'>> = {
      maxInFlightBatches: 1, // Single-threaded for LanceDB integration
      ...(options.batchSize !== undefined && { batchSize: options.batchSize }),
      ...(options.dtype !== undefined && { dtype: options.dtype }),
      ...(options.pooling !== undefined && { pooling: options.pooling }),
      ...(options.normalize !== undefined && { normalize: options.normalize }),
      ...(options.queryPrefix !== undefined && { queryPrefix: options.queryPrefix }),
      ...(options.docPrefix !== undefined && { docPrefix: options.docPrefix }),
    };

    // Use buildEmbeddingConfig for single source of truth (inherits proper queryPrefix, pooling, etc.)
    this.embeddingConfig = buildEmbeddingConfig(model, overrides);

    this.engine = new EmbeddingEngine(this.embeddingConfig);
  }

  /**
   * Initialize the embedding model. Called by LanceDB before embeddings are computed.
   */
  override async init(): Promise<void> {
    this._ndims = await this.engine.ensureDimensions();
  }

  /**
   * Return embedding dimensions. Must call init() first.
   */
  override ndims(): number {
    if (this._ndims === null) {
      throw new Error('HuggingFaceEmbeddingFunction not initialized. Call init() first.');
    }
    return this._ndims;
  }

  /**
   * Return embedding data type (always Float32 for our models).
   */
  override embeddingDataType(): Float32 {
    return new Float32();
  }

  /**
   * Generate embeddings for a batch of texts (documents).
   * Called during table.add() operations.
   */
  override async generateEmbeddings(texts: string[]): Promise<Float32Array[]> {
    return this.engine.embedBatch(texts);
  }

  /**
   * Compute embedding for a single query.
   * Called during table.search(query) operations.
   */
  override async computeQueryEmbeddings(data: string): Promise<number[]> {
    const embedding = await this.engine.embedQuery(data);
    return Array.from(embedding);
  }

  /**
   * Get the model ID for provenance tracking.
   */
  getModelId(): string {
    return this.embeddingConfig.model;
  }

  /**
   * Get the full embedding config.
   */
  getConfig(): EmbeddingConfig {
    return this.embeddingConfig;
  }

  /**
   * Dispose the underlying engine to free resources.
   */
  async dispose(): Promise<void> {
    await this.engine.dispose();
  }
}

// Register with LanceDB's global registry so tables with this embedding function
// can be reopened and used for add/search operations.
getRegistry().register('HuggingFaceEmbeddingFunction')(HuggingFaceEmbeddingFunction);
